import styled, { css } from "styled-components";
import Popover from "common/Popover/Popover.tsx";
import Flex from "common/Boxes/Flex.tsx";
import Title from "common/Typography/Title.tsx";
import {
  APPSTORE_DOWNLOAD_LINK,
  GENERIC_DOWNLOAD_LINK,
  GOOGLE_PLAY_DOWNLOAD_LINK,
} from "common/constants.ts";
import React from "react";
import {
  AppQrCode,
  AppQrCodeIcon,
  AppStore,
  GooglePlay,
} from "common/Icons/Icons.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";
import { phoneMaxWidth, phoneMinWidth } from "common/NewLook/layout.ts";

interface QrPopoverProps {
  isAppStore: boolean;
  isCollapsed?: boolean;
}

const QrPopover = ({ isAppStore, isCollapsed }: QrPopoverProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);

  const popoverContent = (
    <Flex $flexDirection="column" style={{ margin: "4px 0" }}>
      <StyledAppQrCode />
      <StyledTitle level={5}>{t("sider.scan_qr_code")}</StyledTitle>
      <Title level={5}>
        {t("labels.or")}{" "}
        <BoldLink onClick={() => window.open(getLink(), "_blank")}>
          {t("sider.click_here")}
        </BoldLink>
      </Title>
    </Flex>
  );

  const getLink = () => {
    if (isCollapsed) return GENERIC_DOWNLOAD_LINK;
    if (isAppStore) return APPSTORE_DOWNLOAD_LINK;
    else return GOOGLE_PLAY_DOWNLOAD_LINK;
  };

  const getButton = () => {
    if (isCollapsed) return <StyledAppQrCodeIcon />;
    const logo = isAppStore ? <StyledAppStore /> : <StyledGooglePlay />;
    return <ButtonWrapper>{logo}</ButtonWrapper>;
  };

  return (
    <StyledPopover
      placement={isAppStore ? "topLeft" : "topRight"}
      showArrow={false}
      title={false}
      content={popoverContent}
      trigger="click"
      styles={{ root: { width: 225 } }}
    >
      {getButton()}
    </StyledPopover>
  );
};

export default QrPopover;

const StyledPopover = styled(Popover)`
  .ant-popover-inner-content {
    padding: 16px !important;
  }
`;
const StyledAppQrCode = styled(AppQrCode)`
  #qrCode path {
    fill: ${(p) => p.theme.colors.primaryGrey};
  }
  #arrow {
    fill: ${(p) => p.theme.colors.primaryBlue};
  }
  #frame {
    stroke: ${(p) => p.theme.colors.primaryBlue};
  }
  width: 190px;
  height: auto;
`;
const StyledTitle = styled(Title)`
  text-align: center;
  margin: 16px 0;
`;
const BoldLink = styled.span`
  cursor: pointer;
  color: ${(p) => p.theme.colors.primaryText};
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  font-size: ${(p) => p.theme.typography.fontSizes.S};
`;
const DownloadButtonStyle = css`
  fill: ${(p) => p.theme.colors.secondaryGrey};
  cursor: pointer;
  width: 100%;
`;
const StyledAppStore = styled(AppStore)`
  ${DownloadButtonStyle}
  g path {
    fill: ${(p) => p.theme.colors.secondaryGrey};
  }
  @media (min-width: ${phoneMinWidth}) {
    &:hover {
      g path {
        fill: ${(p) => p.theme.colors.primaryGrey};
      }
    }
  }
`;
const StyledGooglePlay = styled(GooglePlay)`
  ${DownloadButtonStyle};
  @media (min-width: ${phoneMinWidth}) {
    &:not(:hover) {
      g path {
        fill: ${(p) => p.theme.colors.secondaryGrey};
      }
    }
  }
  @media (max-width: ${phoneMaxWidth}) {
    g path {
      fill: ${(p) => p.theme.colors.secondaryGrey};
    }
  }
`;
const StyledAppQrCodeIcon = styled(AppQrCodeIcon)`
  width: 18px;
  cursor: pointer;
  margin: 16px auto 16px 13px;
  color: ${(p) => p.theme.colors.primaryGrey};
  &:hover {
    color: ${(p) => p.theme.colors.primaryBlue};
  }
`;
const ButtonWrapper = styled.div`
  border: ${(p) => p.theme.borders.light};
  border-radius: ${(p) => (p.theme.isNewLook ? p.theme.radii.control : 6)}px;
  width: 100%;
  height: 34px;
`;
